import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type { ZeytalTokens } from "@zeytal/types";

import { translateCjs } from "../core/translators/cjs.ts";
import { translateCss } from "../core/translators/css.ts";
import { translateEsm } from "../core/translators/esm.ts";
import { translateJson } from "../core/translators/json.ts";
import { translateLess } from "../core/translators/less.ts";
import { translateSass } from "../core/translators/sass.ts";
import { translateScss } from "../core/translators/scss.ts";
import { translateTs } from "../core/translators/ts.ts";
import { translateTwV4 } from "../core/translators/tw4.ts";

const authorizedTranslations: ZeytalCLIPureConfig["variables"]["formats"]["targets"] =
  ["tw", "ts", "esm", "cjs", "json", "css", "scss", "sass", "less"];

const translationsFilesBasenames = {
  twV4: "variables-tw",
  ts: "variables",
  esm: "variables",
  cjs: "variables",
  json: "variables",
  css: "variables",
  scss: "variables",
  sass: "variables",
  less: "variables",
};

const translationsFilesExtensions = {
  twV4: "css",
  ts: "ts",
  esm: "mjs",
  cjs: "cjs",
  json: "json",
  css: "css",
  scss: "scss",
  sass: "sass",
  less: "less",
};

const authorizedTranslationsFilter = (
  target: ZeytalCLIPureConfig["variables"]["formats"]["targets"][number],
) => authorizedTranslations.includes(target);

const getTranslationFilename = (
  target: ZeytalCLIPureConfig["variables"]["formats"]["targets"][number],
) => {
  let filename;
  switch (target) {
    case "tw":
      filename = `${translationsFilesBasenames.twV4}.${translationsFilesExtensions.twV4}`;
      break;
    case "ts":
      filename = `${translationsFilesBasenames.ts}.${translationsFilesExtensions.ts}`;
      break;
    case "esm":
      filename = `${translationsFilesBasenames.esm}.${translationsFilesExtensions.esm}`;
      break;
    case "cjs":
      filename = `${translationsFilesBasenames.cjs}.${translationsFilesExtensions.cjs}`;
      break;
    case "json":
      filename = `${translationsFilesBasenames.json}.${translationsFilesExtensions.json}`;
      break;
    case "css":
      filename = `${translationsFilesBasenames.css}.${translationsFilesExtensions.css}`;
      break;
    case "scss":
      filename = `${translationsFilesBasenames.scss}.${translationsFilesExtensions.scss}`;
      break;
    case "sass":
      filename = `${translationsFilesBasenames.sass}.${translationsFilesExtensions.sass}`;
      break;
    case "less":
      filename = `${translationsFilesBasenames.less}.${translationsFilesExtensions.less}`;
      break;
    default:
      filename = "";
      break;
  }

  return filename;
};

const translateVarsInTargetLanguage = (
  target: ZeytalCLIPureConfig["variables"]["formats"]["targets"][number],
  userConfig: ZeytalCLIPureConfig,
  variables: ZeytalTokens.TransformedTokensArray,
) => {
  let translation;
  switch (target) {
    case "tw":
      translation = translateTwV4(variables, userConfig);
      break;
    case "ts":
      translation = translateTs(variables);
      break;
    case "esm":
      translation = translateEsm(variables);
      break;
    case "cjs":
      translation = translateCjs(variables);
      break;
    case "json":
      translation = translateJson(variables);
      break;
    case "css":
      translation = translateCss(variables);
      break;
    case "scss":
      translation = translateScss(variables);
      break;
    case "sass":
      translation = translateSass(variables);
      break;
    case "less":
      translation = translateLess(variables);
      break;
    default:
      translation = "";
      break;
  }

  return translation;
};

export const translateVariables = (
  variables: ZeytalTokens.TransformedTokensArray,
  userConfig: ZeytalCLIPureConfig,
) => {
  const { targets } = userConfig.variables.formats;

  const authorizedTargets = [...new Set(targets)].filter(
    authorizedTranslationsFilter,
  ) as ZeytalCLIPureConfig["variables"]["formats"]["targets"];

  // Make an object initialized with targets as key and an empty object as value for the soon to be translated variables values.
  const transformedValuesByTarget = Object.assign(
    {},
    ...authorizedTargets.map((target) => ({
      [getTranslationFilename(target)]: {},
    })),
  ) as Record<string, string>;

  authorizedTargets.forEach((target) => {
    transformedValuesByTarget[getTranslationFilename(target)] =
      translateVarsInTargetLanguage(target, userConfig, variables);
  });

  return transformedValuesByTarget;
};
